import React from 'react';
import { NavbarConfig } from '../types';
import Icon from '@/components/ui/AppIcon';
import { EditableCtaWrapper } from '../EditableCtaWrapper';
import { Section, SectionLayoutConfig } from '@/features/projects/types';
import { ButtonStyleId, BUTTON_STYLES, getButtonStyles, DEFAULT_BUTTON_STYLE, NavbarLayoutId } from '@/constants/layoutTypes';


interface NavbarSectionProps {
    productName: string;
    primaryColor: string;
    navbarConfig: NavbarConfig;
    logoUrl?: string | null;
    badgeText?: string | null;
    showBadge?: boolean;
    onEditNavbar?: () => void;
    isEditing?: boolean;
    onUpdateCta?: (text: string, link: string, action: 'scroll' | 'url') => void;
    sections?: Section[];
    buttonStyle?: ButtonStyleId;
    onButtonStyleChange?: (style: ButtonStyleId) => void;
    layout?: NavbarLayoutId;
    layoutConfig?: SectionLayoutConfig;
}

// ── Shared Sub-Components ──────────────────────────────────────────────────

function Logo({ productName, primaryColor, logoUrl, layoutConfig }: {
    productName: string; primaryColor: string; logoUrl?: string | null; layoutConfig?: SectionLayoutConfig;
}) {
    const showText = layoutConfig?.showLogoText !== false;
    const displayText = layoutConfig?.logoText || productName;
    const logoH = layoutConfig?.logoHeight || 32;

    return (
        <div className="flex items-center gap-2">
            {logoUrl ? (
                <img
                    src={logoUrl}
                    alt={productName}
                    className="w-auto object-contain"
                    style={{ height: `${logoH}px` }}
                />
            ) : (
                <div
                    className="rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: primaryColor, width: `${logoH}px`, height: `${logoH}px` }}
                >
                    {displayText.charAt(0).toUpperCase()}
                </div>
            )}
            {showText && (
                <span className="font-bold text-xl text-gray-900">{displayText}</span>
            )}
        </div>
    );
}

function Badge({ text, primaryColor }: { text: string; primaryColor: string }) {
    return (
        <span
            className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full text-white"
            style={{ backgroundColor: primaryColor }}
        >
            {text}
        </span>
    );
}

function NavLinks({ items, className = '' }: { items: NavbarConfig['menuItems']; className?: string }) {
    return (
        <>
            {items.map((item) => (
                <a
                    key={item.id}
                    href={`#${item.targetSection}`}
                    onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.targetSection)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm ${className}`}
                >
                    {item.label}
                </a>
            ))}
        </>
    );
}

function CtaButton({
    navbarConfig, buttonStyle, primaryColor, isEditing, sections, onUpdateCta, onButtonStyleChange, className = ''
}: {
    navbarConfig: NavbarConfig; buttonStyle: ButtonStyleId; primaryColor: string;
    isEditing: boolean; sections: Section[];
    onUpdateCta?: (t: string, l: string, a: 'scroll' | 'url') => void;
    onButtonStyleChange?: (s: ButtonStyleId) => void;
    className?: string;
}) {
    return (
        <EditableCtaWrapper
            text={navbarConfig.ctaText}
            link={navbarConfig.ctaLink}
            action={navbarConfig.ctaAction}
            buttonStyle={buttonStyle}
            primaryColor={primaryColor}
            isEditing={isEditing}
            sections={sections}
            onUpdate={(text, link, action) => onUpdateCta?.(text, link, action)}
            onStyleChange={onButtonStyleChange}
        >
            <button
                className={`${BUTTON_STYLES.find(s => s.id === buttonStyle)?.className || BUTTON_STYLES[0].className} text-sm !px-5 !py-2.5 ${className}`}
                style={getButtonStyles(buttonStyle, primaryColor)}
                onClick={() => {
                    if (navbarConfig.ctaAction === 'url' && navbarConfig.ctaLink) {
                        window.open(navbarConfig.ctaLink, '_blank');
                    } else if (navbarConfig.ctaLink) {
                        document.getElementById(navbarConfig.ctaLink)?.scrollIntoView({ behavior: 'smooth' });
                    }
                }}
            >
                {navbarConfig.ctaText}
            </button>
        </EditableCtaWrapper>
    );
}

// ── Mobile Menus — One per layout ──────────────────────────────────────────

/** Classic: Card dropdown with chevrons */
function MobileMenuClassic({
    isMenuOpen, setIsMenuOpen, navbarConfig, primaryColor
}: {
    isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void;
    navbarConfig: NavbarConfig; primaryColor: string;
}) {
    return (
        <>
            <button className="@3xl:hidden z-50 p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <Icon name="XMarkIcon" className="w-6 h-6" /> : <Icon name="Bars3Icon" className="w-6 h-6" />}
            </button>
            {isMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] z-40 animate-in fade-in duration-200" onClick={() => setIsMenuOpen(false)} />
                    <div className="absolute top-16 right-4 left-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 flex flex-col gap-4 z-50 origin-top animate-in zoom-in-95 duration-200">
                        <div className="flex flex-col gap-1">
                            {navbarConfig.menuItems.map((item) => (
                                <a key={item.id} href={`#${item.targetSection}`}
                                    onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById(item.targetSection)?.scrollIntoView({ behavior: 'smooth' }); }}
                                    className="p-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-colors font-semibold flex items-center justify-between group">
                                    {item.label}
                                    <Icon name="ChevronRightIcon" className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                </a>
                            ))}
                        </div>
                        <div className="border-t border-gray-100 my-1" />
                        <button className="w-full px-5 py-3.5 text-white rounded-xl font-bold text-center shadow-md hover:shadow-lg transition-all" style={{ backgroundColor: primaryColor }}
                            onClick={() => { setIsMenuOpen(false); if (navbarConfig.ctaAction === 'url' && navbarConfig.ctaLink) window.open(navbarConfig.ctaLink, '_blank'); else if (navbarConfig.ctaLink) document.getElementById(navbarConfig.ctaLink)?.scrollIntoView({ behavior: 'smooth' }); }}>
                            {navbarConfig.ctaText}
                        </button>
                    </div>
                </>
            )}
        </>
    );
}

/** Centered: Full-screen overlay */
function MobileMenuFullscreen({
    isMenuOpen, setIsMenuOpen, navbarConfig, primaryColor
}: {
    isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void;
    navbarConfig: NavbarConfig; primaryColor: string;
}) {
    return (
        <>
            <button className="@3xl:hidden z-50 p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <Icon name="XMarkIcon" className="w-6 h-6" /> : <Icon name="Bars3Icon" className="w-6 h-6" />}
            </button>
            {isMenuOpen && (
                <div className="fixed inset-0 bg-white z-40 flex flex-col items-center justify-center gap-6 animate-in fade-in duration-200">
                    <button className="absolute top-5 right-5 p-2 text-gray-600 hover:bg-gray-100 rounded-lg" onClick={() => setIsMenuOpen(false)}>
                        <Icon name="XMarkIcon" className="w-7 h-7" />
                    </button>
                    {navbarConfig.menuItems.map((item) => (
                        <a key={item.id} href={`#${item.targetSection}`}
                            onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById(item.targetSection)?.scrollIntoView({ behavior: 'smooth' }); }}
                            className="text-2xl font-bold text-gray-800 hover:text-gray-900 transition-colors">
                            {item.label}
                        </a>
                    ))}
                    <div className="w-16 h-px bg-gray-200 my-2" />
                    <button className="px-8 py-3.5 text-white rounded-xl font-bold text-center shadow-md hover:shadow-lg transition-all text-lg" style={{ backgroundColor: primaryColor }}
                        onClick={() => { setIsMenuOpen(false); if (navbarConfig.ctaAction === 'url' && navbarConfig.ctaLink) window.open(navbarConfig.ctaLink, '_blank'); else if (navbarConfig.ctaLink) document.getElementById(navbarConfig.ctaLink)?.scrollIntoView({ behavior: 'smooth' }); }}>
                        {navbarConfig.ctaText}
                    </button>
                </div>
            )}
        </>
    );
}

/** Minimal: Slide-down simple list */
function MobileMenuMinimal({
    isMenuOpen, setIsMenuOpen, navbarConfig, primaryColor
}: {
    isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void;
    navbarConfig: NavbarConfig; primaryColor: string;
}) {
    return (
        <>
            <button className="@3xl:hidden z-50 p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <Icon name="XMarkIcon" className="w-6 h-6" /> : <Icon name="Bars3Icon" className="w-6 h-6" />}
            </button>
            {isMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-white/50 backdrop-blur-[1px] z-40 animate-in fade-in duration-150" onClick={() => setIsMenuOpen(false)} />
                    <div className="absolute top-14 right-4 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 origin-top-right animate-in zoom-in-95 duration-150">
                        {navbarConfig.menuItems.map((item) => (
                            <a key={item.id} href={`#${item.targetSection}`}
                                onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById(item.targetSection)?.scrollIntoView({ behavior: 'smooth' }); }}
                                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 font-medium transition-colors">
                                {item.label}
                            </a>
                        ))}
                        <div className="border-t border-gray-100 my-1 mx-4" />
                        <div className="px-4 py-2">
                            <button className="w-full px-4 py-2.5 text-white rounded-lg font-semibold text-sm text-center transition-all" style={{ backgroundColor: primaryColor }}
                                onClick={() => { setIsMenuOpen(false); if (navbarConfig.ctaAction === 'url' && navbarConfig.ctaLink) window.open(navbarConfig.ctaLink, '_blank'); else if (navbarConfig.ctaLink) document.getElementById(navbarConfig.ctaLink)?.scrollIntoView({ behavior: 'smooth' }); }}>
                                {navbarConfig.ctaText}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

/** Floating: Pill-style dropdown */
function MobileMenuFloating({
    isMenuOpen, setIsMenuOpen, navbarConfig, primaryColor
}: {
    isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void;
    navbarConfig: NavbarConfig; primaryColor: string;
}) {
    return (
        <>
            <button className="@3xl:hidden z-50 p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <Icon name="XMarkIcon" className="w-6 h-6" /> : <Icon name="Bars3Icon" className="w-6 h-6" />}
            </button>
            {isMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] z-40 animate-in fade-in duration-150" onClick={() => setIsMenuOpen(false)} />
                    <div className="absolute top-14 left-4 right-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/60 p-5 z-50 origin-top animate-in zoom-in-95 duration-200">
                        <div className="flex flex-col gap-1">
                            {navbarConfig.menuItems.map((item) => (
                                <a key={item.id} href={`#${item.targetSection}`}
                                    onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById(item.targetSection)?.scrollIntoView({ behavior: 'smooth' }); }}
                                    className="p-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-colors font-semibold text-center">
                                    {item.label}
                                </a>
                            ))}
                        </div>
                        <div className="border-t border-gray-200/60 my-3" />
                        <button className="w-full px-5 py-3 text-white rounded-full font-bold text-center shadow-md hover:shadow-lg transition-all" style={{ backgroundColor: primaryColor }}
                            onClick={() => { setIsMenuOpen(false); if (navbarConfig.ctaAction === 'url' && navbarConfig.ctaLink) window.open(navbarConfig.ctaLink, '_blank'); else if (navbarConfig.ctaLink) document.getElementById(navbarConfig.ctaLink)?.scrollIntoView({ behavior: 'smooth' }); }}>
                            {navbarConfig.ctaText}
                        </button>
                    </div>
                </>
            )}
        </>
    );
}

// ── Layout 1: Classic ──────────────────────────────────────────────────────

function NavbarClassic(props: NavbarSectionProps & { isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void }) {
    const { productName, primaryColor, navbarConfig, logoUrl, badgeText, showBadge, isEditing = false, sections = [], buttonStyle = DEFAULT_BUTTON_STYLE, onUpdateCta, onButtonStyleChange, isMenuOpen, setIsMenuOpen, layoutConfig } = props;
    return (
        <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 z-50 relative">
                <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
            </div>
            <div className="hidden @3xl:flex items-center gap-8">
                <NavLinks items={navbarConfig.menuItems} />
            </div>
            <div className="hidden @3xl:block">
                <CtaButton navbarConfig={navbarConfig} buttonStyle={buttonStyle} primaryColor={primaryColor} isEditing={isEditing} sections={sections} onUpdateCta={onUpdateCta} onButtonStyleChange={onButtonStyleChange} />
            </div>
            <MobileMenuClassic isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navbarConfig={navbarConfig} primaryColor={primaryColor} />
        </div>
    );
}

// ── Layout 2: Centered ─────────────────────────────────────────────────────

function NavbarCentered(props: NavbarSectionProps & { isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void }) {
    const { productName, primaryColor, navbarConfig, logoUrl, badgeText, showBadge, isEditing = false, sections = [], buttonStyle = DEFAULT_BUTTON_STYLE, onUpdateCta, onButtonStyleChange, isMenuOpen, setIsMenuOpen, layoutConfig } = props;
    return (
        <div className="max-w-6xl mx-auto">
            <div className="hidden @3xl:flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                    <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                    {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
                </div>
                <div className="flex items-center gap-8">
                    <NavLinks items={navbarConfig.menuItems} />
                    <div className="w-px h-5 bg-gray-200" />
                    <CtaButton navbarConfig={navbarConfig} buttonStyle={buttonStyle} primaryColor={primaryColor} isEditing={isEditing} sections={sections} onUpdateCta={onUpdateCta} onButtonStyleChange={onButtonStyleChange} />
                </div>
            </div>
            <div className="flex @3xl:hidden items-center justify-between">
                <div className="flex items-center gap-2 z-50 relative">
                    <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                    {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
                </div>
                <MobileMenuFullscreen isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navbarConfig={navbarConfig} primaryColor={primaryColor} />
            </div>
        </div>
    );
}

// ── Layout 3: Minimal ──────────────────────────────────────────────────────

function NavbarMinimal(props: NavbarSectionProps & { isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void }) {
    const { productName, primaryColor, navbarConfig, logoUrl, badgeText, showBadge, isEditing = false, sections = [], buttonStyle = DEFAULT_BUTTON_STYLE, onUpdateCta, onButtonStyleChange, isMenuOpen, setIsMenuOpen, layoutConfig } = props;
    return (
        <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 z-50 relative">
                <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
            </div>
            <div className="hidden @3xl:flex items-center gap-4">
                {navbarConfig.menuItems.length > 0 && (
                    <a href={`#${navbarConfig.menuItems[0].targetSection}`}
                        onClick={(e) => { e.preventDefault(); document.getElementById(navbarConfig.menuItems[0].targetSection)?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors">
                        Learn more ↓
                    </a>
                )}
                <CtaButton navbarConfig={navbarConfig} buttonStyle={buttonStyle} primaryColor={primaryColor} isEditing={isEditing} sections={sections} onUpdateCta={onUpdateCta} onButtonStyleChange={onButtonStyleChange} />
            </div>
            <MobileMenuMinimal isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navbarConfig={navbarConfig} primaryColor={primaryColor} />
        </div>
    );
}

// ── Layout 4: Floating Pill ────────────────────────────────────────────────

function NavbarFloating(props: NavbarSectionProps & { isMenuOpen: boolean; setIsMenuOpen: (v: boolean) => void }) {
    const { productName, primaryColor, navbarConfig, logoUrl, badgeText, showBadge, isEditing = false, sections = [], buttonStyle = DEFAULT_BUTTON_STYLE, onUpdateCta, onButtonStyleChange, isMenuOpen, setIsMenuOpen, layoutConfig } = props;
    return (
        <div className="max-w-5xl mx-auto">
            {/* Desktop: floating pill */}
            <div className="hidden @3xl:flex items-center justify-between bg-white/80 backdrop-blur-xl rounded-full px-6 py-2.5 shadow-lg shadow-black/[0.04] border border-gray-200/60">
                <div className="flex items-center gap-2">
                    <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                    {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
                </div>
                <div className="flex items-center gap-6">
                    <NavLinks items={navbarConfig.menuItems} />
                </div>
                <CtaButton navbarConfig={navbarConfig} buttonStyle={buttonStyle} primaryColor={primaryColor} isEditing={isEditing} sections={sections} onUpdateCta={onUpdateCta} onButtonStyleChange={onButtonStyleChange} className="!py-2 !px-4 !text-xs" />
            </div>
            {/* Mobile: floating pill bar */}
            <div className="flex @3xl:hidden items-center justify-between bg-white/90 backdrop-blur-xl rounded-full px-4 py-2 shadow-lg shadow-black/[0.04] border border-gray-200/60">
                <div className="flex items-center gap-2 z-50 relative">
                    <Logo productName={productName} primaryColor={primaryColor} logoUrl={logoUrl} layoutConfig={layoutConfig} />
                    {showBadge && badgeText && <Badge text={badgeText} primaryColor={primaryColor} />}
                </div>
                <MobileMenuFloating isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} navbarConfig={navbarConfig} primaryColor={primaryColor} />
            </div>
        </div>
    );
}

// ── Main Export ─────────────────────────────────────────────────────────────

export function NavbarSection(props: NavbarSectionProps) {
    const { layout = 'classic', onEditNavbar } = props;
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    React.useEffect(() => {
        if (isMenuOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isMenuOpen]);

    const isFloating = layout === 'floating';

    return (
        <nav className={`sticky top-0 z-50 group relative ${isFloating ? 'bg-transparent px-4 py-3' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-4'}`}>
            {onEditNavbar && (
                <button onClick={onEditNavbar}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center gap-2 pointer-events-auto">
                    <Icon name="PencilSquareIcon" className="w-4 h-4" />
                    Edit Navbar
                </button>
            )}
            {layout === 'centered' ? <NavbarCentered {...props} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
                : layout === 'minimal' ? <NavbarMinimal {...props} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
                : layout === 'floating' ? <NavbarFloating {...props} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
                : <NavbarClassic {...props} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
        </nav>
    );
}
